import { supabase } from '../supabase';

export interface PersonalityTraits {
  openness: number; // 0-100
  conscientiousness: number;
  extraversion: number;
  agreeableness: number;
  neuroticism: number;
}

export interface WorkStyle {
  independence: number;
  collaboration: number;
  structure: number;
  flexibility: number;
  innovation: number;
  stability: number;
}

export interface PersonalityAssessment {
  id: string;
  user_id: string;
  openness: number;
  conscientiousness: number;
  extraversion: number;
  agreeableness: number;
  neuroticism: number;
  work_style: WorkStyle;
  communication_preference: string[];
  meeting_frequency_preference: string;
  core_values: string[];
  motivators: any;
  completed_at: string;
}

export interface CompanyCulture {
  id: string;
  employer_id: string;
  innovation_focus: number;
  collaboration_emphasis: number;
  work_life_balance: number;
  hierarchy_level: number;
  risk_tolerance: number;
  pace_of_work: number;
  culture_keywords: string[];
  core_values: string[];
  primary_work_mode: string;
}

export interface MatchScore {
  job_id: string;
  overall_score: number;
  skills_score: number;
  experience_score: number;
  culture_fit_score: number;
  personality_fit_score: number;
  location_score: number;
  match_explanation?: string;
  strengths?: string[];
  potential_concerns?: string[];
}

/**
 * Save personality assessment for a user
 */
export async function savePersonalityAssessment(
  userId: string,
  assessment: Partial<PersonalityAssessment>
): Promise<PersonalityAssessment | null> {
  try {
    const { data, error } = await supabase
      .from('personality_assessments')
      .upsert({
        user_id: userId,
        ...assessment,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving personality assessment:', error);
    return null;
  }
}

/**
 * Get personality assessment for a user
 */
export async function getPersonalityAssessment(
  userId: string
): Promise<PersonalityAssessment | null> {
  try {
    const { data, error } = await supabase
      .from('personality_assessments')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching personality assessment:', error);
    return null;
  }
}

/**
 * Save company culture profile
 */
export async function saveCompanyCulture(
  employerId: string,
  culture: Partial<CompanyCulture>
): Promise<CompanyCulture | null> {
  try {
    const { data, error } = await supabase
      .from('company_cultures')
      .upsert({
        employer_id: employerId,
        ...culture,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving company culture:', error);
    return null;
  }
}

/**
 * Get company culture for an employer
 */
export async function getCompanyCulture(
  employerId: string
): Promise<CompanyCulture | null> {
  try {
    const { data, error} = await supabase
      .from('company_cultures')
      .select('*')
      .eq('employer_id', employerId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching company culture:', error);
    return null;
  }
}

/**
 * Calculate match score between user and job
 */
export async function calculateMatchScore(
  userId: string,
  jobId: string
): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('calculate_match_score', {
      p_user_id: userId,
      p_job_id: jobId,
    });

    if (error) throw error;
    return data || 0;
  } catch (error) {
    console.error('Error calculating match score:', error);
    return 0;
  }
}

/**
 * Get top job matches for a user
 */
export async function getTopMatchesForUser(
  userId: string,
  limit: number = 50
): Promise<MatchScore[]> {
  try {
    const { data, error } = await supabase.rpc('get_top_matches_for_user', {
      p_user_id: userId,
      p_limit: limit,
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching top matches:', error);
    return [];
  }
}

/**
 * Get detailed match score for a specific job
 */
export async function getMatchScore(
  userId: string,
  jobId: string
): Promise<MatchScore | null> {
  try {
    const { data, error } = await supabase
      .from('match_scores')
      .select('*')
      .eq('user_id', userId)
      .eq('job_id', jobId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching match score:', error);
    return null;
  }
}

/**
 * Submit employer feedback on a candidate
 */
export async function submitEmployerFeedback(
  employerId: string,
  applicantId: string,
  jobId: string,
  applicationId: string,
  feedback: {
    outcome: 'hired' | 'rejected' | 'interviewed' | 'withdrawn';
    skills_match_rating?: number;
    culture_fit_rating?: number;
    communication_rating?: number;
    performance_rating?: number;
    retention_months?: number;
    would_hire_again?: boolean;
    feedback_text?: string;
    tags?: string[];
  }
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('employer_feedback')
      .insert({
        employer_id: employerId,
        applicant_id: applicantId,
        job_id: jobId,
        application_id: applicationId,
        ...feedback,
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error submitting employer feedback:', error);
    return false;
  }
}

/**
 * Generate match explanation based on scores
 */
export function generateMatchExplanation(match: MatchScore): {
  explanation: string;
  strengths: string[];
  concerns: string[];
} {
  const strengths: string[] = [];
  const concerns: string[] = [];

  // Skills analysis
  if (match.skills_score >= 80) {
    strengths.push('Strong skills match - your expertise aligns well with requirements');
  } else if (match.skills_score >= 60) {
    strengths.push('Good skills match with room for growth');
  } else if (match.skills_score < 50) {
    concerns.push('Some skill gaps - consider upskilling in key areas');
  }

  // Culture fit analysis
  if (match.culture_fit_score >= 80) {
    strengths.push('Excellent culture fit - your work style matches the company');
  } else if (match.culture_fit_score < 50) {
    concerns.push('Different work culture - research company values carefully');
  }

  // Experience analysis
  if (match.experience_score >= 80) {
    strengths.push('Your experience level is a great match');
  } else if (match.experience_score < 50) {
    concerns.push('Experience level may not align perfectly');
  }

  // Location analysis
  if (match.location_score >= 90) {
    strengths.push('Location is ideal for you');
  } else if (match.location_score < 60) {
    concerns.push('Consider location and remote work options');
  }

  // Generate overall explanation
  let explanation = '';
  if (match.overall_score >= 80) {
    explanation = 'This is an excellent match! Your profile aligns very well with this opportunity.';
  } else if (match.overall_score >= 60) {
    explanation = 'This is a good match with strong potential. Review the details to ensure alignment.';
  } else {
    explanation = 'This could be a stretch opportunity. Consider the gaps and growth potential.';
  }

  return { explanation, strengths, concerns };
}

/**
 * Calculate Big Five personality from quiz responses
 */
export function calculatePersonalityTraits(responses: Record<string, number>): PersonalityTraits {
  // This is a simplified calculation
  // In production, you'd use validated personality assessment scoring

  const opennessQuestions = ['creative', 'curious', 'imaginative', 'artistic'];
  const conscientiousnessQuestions = ['organized', 'reliable', 'thorough', 'disciplined'];
  const extraversionQuestions = ['outgoing', 'energetic', 'social', 'assertive'];
  const agreeablenessQuestions = ['friendly', 'compassionate', 'cooperative', 'trustworthy'];
  const neuroticismQuestions = ['anxious', 'moody', 'stressed', 'worried'];

  const calculateTrait = (questions: string[]) => {
    const scores = questions
      .map(q => responses[q])
      .filter(score => score !== undefined);

    if (scores.length === 0) return 50;

    const avg = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    return Math.round(avg * 20); // Convert 1-5 scale to 0-100
  };

  return {
    openness: calculateTrait(opennessQuestions),
    conscientiousness: calculateTrait(conscientiousnessQuestions),
    extraversion: calculateTrait(extraversionQuestions),
    agreeableness: calculateTrait(agreeablenessQuestions),
    neuroticism: calculateTrait(neuroticismQuestions),
  };
}
